import {
  showSuccess,
  showConfirm,
  showCustom,
  showLoading,
  hideLoading,
} from "@/assets/js/utils/alertService";
import api from "@/assets/js/utils/api.js";

export default {
  name: "ReturnManagement",

  data() {
    return {
      activeStatusDropdown: null,
      searchQuery: "",
      activeFilter: "all",
      showDetailModal: false,
      showRejectModal: false,
      showStats: false,
      selectedReturn: null,
      rejectingReturn: null,
      rejectReason: "",
      activeDropdown: null,

      filters: [
        { label: "全部退貨", value: "all", count: null }, // 控制：全部
        { label: "審核中", value: "審核中", count: null }, // 控制：審核中
        { label: "已同意", value: "已同意", count: null }, // 控制：已同意
        { label: "已完成", value: "已完成", count: null }, // 控制：已完成
        { label: "已拒絕", value: "已拒絕", count: null }, // 控制：已拒絕
      ],

      returns: [], // 控制：退貨列表

      currentPage: 1,
      itemsPerPage: 5,
    };
  },

  computed: {
    filteredReturns() {
      let result = this.returns;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(
          (item) =>
            item.returnNumber.toLowerCase().includes(query) ||
            item.orderNumber.toLowerCase().includes(query) ||
            item.customerName.toLowerCase().includes(query) ||
            item.contactPerson.toLowerCase().includes(query),
        );
      }

      if (this.activeFilter !== "all") {
        result = result.filter((item) => item.status === this.activeFilter);
      }

      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return result.slice(start, end);
    },

    totalPages() {
      const total = this.returns.filter(
        (item) =>
          this.activeFilter === "all" || item.status === this.activeFilter,
      ).length;
      return Math.ceil(total / this.itemsPerPage);
    },

    stats() {
      return {
        pending: this.returns.filter((r) => r.status === "pending").length,
        approved: this.returns.filter((r) => r.status === "approved").length,
        processing: this.returns.filter((r) => r.status === "processing")
          .length,
        completed: this.returns.filter((r) => r.status === "completed").length,
        rejected: this.returns.filter((r) => r.status === "rejected").length,
      };
    },
  },

  watch: {
    filteredReturns() {
      this.updateFilterCounts();
    },
  },

  async mounted() {
    document.addEventListener("click", () => {
      this.activeStatusDropdown = null;
      this.activeDropdown = null;
    });

    await this.loadReturnList();
    this.updateFilterCounts();
  },

  methods: {
    async loadReturnList() {
      // 控制：載入退貨列表
      try {
        const res = await api.get("/return/list"); // 控制：呼叫後端 API

        if (!res.data?.success) {
          // 控制：檢查成功旗標
          showCustom({
            icon: "error",
            title: "取得資料失敗",
            text: res.data?.message || "後端無回傳資料",
          });
          return; // 控制：失敗就中止
        }

        const rawList = res.data.data; // 控制：原始資料列表

        this.returns = rawList.map((r) => ({
          // 控制：轉成前端使用格式
          id: r.Id, // 控制：退貨單ID
          returnNumber: r.OrderReturnNo, // 控制：用退貨單號
          orderNumber: r.OrderNo, // 控制：訂單編號
          orderId: r.OrderId,
          customerName: r.CompanyName, // 公司名稱
          contactPerson: r.FullName, // 聯絡人姓名
          phone: r.PhoneNumber, // 控制：客戶電話
          companyName: r.CompanyName, // 控制：公司名稱

          type: r.Type, // 控制：退/換貨類型（中文）
          status: r.Status, // ✅ 控制：直接用後端中文狀態
          applyDate: r.CreatedDate?.split("T")[0], // 控制：申請日期
          processedDate: r.UpdatedDate?.split("T")[0] || null, // 控制：處理日期

          reason: r.Reason, // 控制：原因
          note: r.AdminNote, // 控制：管理員備註

          refundAmount: r.RefundAmount || 0, // 控制：退款金額（改用 RefundAmount）
          items: (r.Items || []).map((i) => ({
            id: i.Id,
            // 控制：商品名稱（價格為0自動標記贈品）
            productName:
              i.Price === 0 ? `${i.ProductName}（贈品）` : i.ProductName,
            price: i.Price, // 單價
            quantity: i.Quantity, // 數量
            subTotal: i.SubTotal, // 小計
          })),

          returnDetails: (r.ReturnDetails || []).map((d) => ({
            id: d.Id,
            productName: d.ProductName,
            variantName: d.VariantName,
            quantity: d.Quantity,
            refundAmount: d.RefundAmount,
            reason: d.Reason,
            originalPrice: d.OriginalPrice,
          })),

          history: [], // 控制：暫時不使用歷程
        }));
      } catch (err) {
        console.error(err); // 控制：錯誤輸出
        showCustom({
          icon: "error",
          title: "錯誤",
          text: "無法取得退貨資料",
        });
      }
    },

    mapStatus(status) {
      const map = {
        approved: "已同意",
        completed: "已完成",
        rejected: "已拒絕",
        pending: "審核中",
      };
      return map[status] || status;
    },
    handleSearch() {
      this.currentPage = 1;
    },

    getTypeText(type) {
      // 控制：顯示中文標籤
      const map = {
        退貨: "退貨",
        換貨: "換貨",
      };
      return map[type] || type;
    },

    getTypeClass(type) {
      // 控制：對應到你的 CSS class
      return `type-${type}`;
    },

    updateFilterCounts() {
      this.filters = this.filters.map((f) => {
        if (f.value === "all") {
          return { ...f, count: this.returns.length };
        }
        return {
          ...f,
          count: this.returns.filter((r) => r.status === f.value).length,
        };
      });
    },

    toggleDropdown(returnId) {
      this.activeDropdown = this.activeDropdown === returnId ? null : returnId;
    },

    toggleStatusDropdown(returnId) {
      if (this.activeStatusDropdown === returnId) {
        this.activeStatusDropdown = null;
      } else {
        this.activeStatusDropdown = returnId;
      }
    },

    getStatusClass(status) {
      const classes = {
        審核中: "status-審核中",
        已同意: "status-已同意",
        準備中: "status-準備中",
        已完成: "status-已完成",
        已拒絕: "status-已拒絕",
      };

      return classes[status] || "";
    },
    getItemsPreview(returnItem) {
      return returnItem.items
        .map((item) => `${item.productName} x${item.quantity}`)
        .join("\n");
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;
    },

    viewReturnDetail(returnItem) {
      this.selectedReturn = { ...returnItem };
      this.showDetailModal = true;
    },

    viewOrder(returnItem) {
      this.$router.push(`/admin/order?id=${returnItem.orderId}`);
    },
    viewImage(imageUrl) {
      window.open(imageUrl, "_blank");
    },

    formatNumber(num) {
      if (num == null || isNaN(num)) return "0"; // 防堵 undefined/null/NaN
      return Number(num).toLocaleString("zh-TW");
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },

    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },

    async updateReturnStatus(returnItem, newStatus) {
      const statusText = this.mapStatus(newStatus); // 把英文轉成中文

      const confirmed = await showConfirm(
        `確定要將退貨單「${returnItem.orderNumber}」狀態變更為「${statusText}」嗎？`,
      );
      if (!confirmed.isConfirmed) return;

      showLoading("正在更新狀態...");

      try {
        // 🔵 送給後端
        const res = await api.post("/return/update-status", {
          orderNo: returnItem.orderNumber, // 後端用訂單編號
          status: statusText, // 後端使用中文狀態
          adminNote: returnItem.note || "", // 傳送管理員備註
        });

        hideLoading();

        if (!res.data?.success) {
          showCustom({
            icon: "error",
            title: "更新失敗",
            text: res.data?.message || "後端回傳失敗",
          });
          return;
        }

        // 🔵 後端成功 → 更新前端資料
        returnItem.status = statusText;
        returnItem.processedDate = new Date().toISOString().split("T")[0];

        showSuccess(`狀態已更新為「${statusText}」！`);

        // 若正在查看詳情 → 同步更新 modal
        if (this.selectedReturn && this.selectedReturn.id === returnItem.id) {
          this.selectedReturn.status = statusText;
          this.selectedReturn.processedDate = returnItem.processedDate;
        }

        this.updateFilterCounts();
      } catch (err) {
        hideLoading();
        console.error(err);
        showCustom({
          icon: "error",
          title: "更新失敗",
          text: "與伺服器連線失敗，請稍後重試",
        });
      }
    },
    async approveReturn(returnItem) {
      await this.updateReturnStatus(returnItem, "approved");
    },

    rejectReturn(returnItem) {
      this.rejectingReturn = returnItem;
      this.rejectReason = "";
      this.showRejectModal = true;
    },

    async confirmReject() {
      if (!this.rejectReason.trim()) {
        showCustom({
          icon: "warning",
          title: "請輸入拒絕原因",
          text: "拒絕退貨申請必須提供原因說明",
        });
        return;
      }

      showLoading("正在處理...");

      setTimeout(() => {
        this.rejectingReturn.status = "rejected";
        this.rejectingReturn.processedDate = new Date()
          .toISOString()
          .split("T")[0];
        this.rejectingReturn.note = `拒絕原因: ${this.rejectReason}`;

        if (!this.rejectingReturn.history) {
          this.rejectingReturn.history = [];
        }
        this.rejectingReturn.history.push({
          date: new Date().toLocaleString("zh-TW"),
          action: "拒絕退貨申請",
          type: "rejected",
          operator: "管理員",
          note: this.rejectReason,
        });

        hideLoading();
        showSuccess("已拒絕退貨申請", "系統將自動通知客戶");

        this.showRejectModal = false;
        this.showDetailModal = false;
        this.rejectingReturn = null;
        this.rejectReason = "";

        this.updateFilterCounts();
      }, 800);
    },

    async processReturn(returnItem) {
      await this.updateReturnStatus(returnItem, "processing");
    },

    async completeReturn(returnItem) {
      await this.updateReturnStatus(returnItem, "completed");
    },

    async saveReturnNote(returnItem) {
      if (!returnItem.note) {
        showCustom({
          icon: "warning",
          title: "請輸入註記內容",
          text: "退貨註記不能為空",
        });
        return;
      }

      showLoading("正在儲存註記...");

      try {
        const res = await api.post("/return/update-status", {
          orderNo: returnItem.orderNumber, // 用訂單編號
          status: returnItem.status, // 保持現有狀態
          adminNote: returnItem.note, // 傳送註記內容
        });

        hideLoading();

        if (!res.data?.success) {
          showCustom({
            icon: "error",
            title: "儲存失敗",
            text: res.data?.message || "無法儲存註記",
          });
          return;
        }

        showSuccess("註記已儲存！");

        // 在列表中同步更新
        const itemInList = this.returns.find((r) => r.id === returnItem.id);
        if (itemInList) {
          itemInList.note = returnItem.note;
        }
      } catch (err) {
        hideLoading();
        console.error(err);
        showCustom({
          icon: "error",
          title: "儲存失敗",
          text: "與伺服器連線失敗，請稍後重試",
        });
      }
    },
  },
};
